/**
 * Pure helpers for the Virtual Coach Knock in-app feed (SCO-371) and the producer
 * banner + bell surface. Kept free of the `@knocklabs/client` runtime so they
 * unit-test with plain objects.
 */
import { z } from 'zod';
import { allowedFeedHref } from './feedLinks';
/** Knock workflow key for the interactive morning check-in (matches the backend trigger). */
export var MORNING_CHECKIN_WORKFLOW_KEY = 'morning-checkin';
/**
 * The producer-stage ask (SCO-470, escalation ADR §1) — delivered TO the
 * producer, answered BY the producer. Distinct from the coach's
 * `morning-checkin`: a tap here is producer-confirmed attendance, which the
 * backend attributes by workflow key (`parseMorningCheckinInteraction` derives
 * `source: 'producer'`), not by who happens to be logged in.
 */
export var PRODUCER_MORNING_CHECKIN_WORKFLOW_KEY = 'producer-morning-checkin';
/** Rung-1 scoring nudge (SCO-504) — the one workflow whose tap NAVIGATES. */
export var PRODUCER_SCORING_NUDGE_WORKFLOW_KEY = 'producer-scoring-nudge';
/**
 * Workflows delivered TO producers (recipient = producer). `coach-nudge` is the
 * coach's one-tap send — despite the name, the producer receives it. Everything
 * else (morning-checkin, setup/weekend prompts, recaps) is coach/admin-facing
 * and must never surface on the producer banner or bell.
 */
export var PRODUCER_FACING_WORKFLOW_KEYS = [
    'producer-nudge',
    'coach-nudge',
    PRODUCER_MORNING_CHECKIN_WORKFLOW_KEY,
    // Rung-2 warning shot (SCO-505): the consequence-naming card between the
    // unanswered ask and the coach escalation.
    'producer-checkin-warning',
    // Rung-1 scoring nudge (SCO-504): present-but-silent, deep-linked to the
    // scoring screen. A nudge, not a ladder card — no roll-off entry.
    PRODUCER_SCORING_NUDGE_WORKFLOW_KEY,
    // VC-02 (plan #723): the coach's kickoff and midday in the new voice, each
    // sent with an in-app feed step. Their office forms (`*-office`) are
    // coach-facing and stay off. `gong-producer` stays off too (James
    // 2026-10-18, Q6): it would turn the gong card on for producers in every
    // office on deploy; it comes back with the voice for everyone or behind its
    // own switch. VC-15b later generates this list from the backend's
    // notification catalog.
    'kickoff-producer',
    'midday-producer'
];
/**
 * Check-in surfaces the client day roll-off still governs. Owner ruling 2
 * (2026-08-11) removed the COACH-facing cards from this list: a coach-facing
 * question carries no clock and sits open until the coach answers it —
 * nothing (server sweep or client roll-off) self-resolves or hides it on the
 * coach's behalf. `morning-checkin` and `morning-checkin-digest` cards now
 * leave the feed only via an answer (`interacted_at`) or server archival
 * (`archived_at` — set when the row resolves, e.g. by the producer's own
 * activity). The rung-2 warning card stays: it is producer-facing and
 * informational, and stale "escalation is coming" copy must not outlive its
 * day.
 */
export var CHECKIN_ROLLOFF_WORKFLOW_KEYS = ['producer-checkin-warning'];
/** Knock names the in-app action_url block this; it is metadata, not body text. */
var ACTION_URL_BLOCK_NAME = 'action_url';
/** Whether a feed item came from the morning-checkin workflow (renders Yes/No). */
export function isMorningCheckinItem(item) {
    var _a;
    return ((_a = item.source) === null || _a === void 0 ? void 0 : _a.key) === MORNING_CHECKIN_WORKFLOW_KEY;
}
/**
 * Whether a feed item is the producer's own attendance ask. These render
 * Yes/No instead of the banner's tap-to-dismiss: an `acknowledged` tap is a
 * server-side no-op (`mapKnockActionToStatus` returns null for anything that
 * isn't yes/no), but it still sets `interacted_at` — which would clear the
 * banner and leave the producer no way to answer.
 */
export function isProducerCheckinItem(item) {
    var _a;
    return ((_a = item.source) === null || _a === void 0 ? void 0 : _a.key) === PRODUCER_MORNING_CHECKIN_WORKFLOW_KEY;
}
/** Whether the item is any check-in-ladder surface the day roll-off governs. */
export function isCheckinRolloffItem(item) {
    var _a;
    return CHECKIN_ROLLOFF_WORKFLOW_KEYS.includes((_a = item.source) === null || _a === void 0 ? void 0 : _a.key);
}
var insertedAtDate = function (item) { return new Date(item.inserted_at); };
var isSameLocalDay = function (a, b) { return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate(); };
/** 'YYYY-MM-DD' in the viewer's local timezone. */
var localDateString = function (d) {
    var pad = function (n) { return String(n).padStart(2, '0'); };
    return "".concat(d.getFullYear(), "-").concat(pad(d.getMonth() + 1), "-").concat(pad(d.getDate()));
};
/**
 * Trigger data is external input — parse, never trust (SCO-406 lesson).
 * Calendar round-trip, not just shape (Codex round 2): a patterned-but-bogus
 * value like 9999-99-99 must fall back to inserted_at, not survive forever.
 */
var CheckinTriggerDataSchema = z.object({
    local_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .refine(function (value) {
        var _a = value.split('-').map(Number), year = _a[0], month = _a[1], day = _a[2];
        var d = new Date(Date.UTC(year, month - 1, day));
        return (d.getUTCFullYear() === year &&
            d.getUTCMonth() === month - 1 &&
            d.getUTCDate() === day);
    }, 'not a real calendar date')
});
/**
 * Whether a check-in card is still within its ACCOUNT-local day (Codex round
 * 1, #407 P1). The card's lifecycle is keyed on the account's `local_date`
 * (carried in trigger data), not the viewer's clock — comparing `inserted_at`
 * in browser-local time loses the card early for a coach east of the account
 * and keeps it late for one west of it. The viewer's timezone is still the
 * only "now" the client has, so the rule is deliberately DROP-LATE: the card
 * survives until the viewer's own calendar has moved past `local_date` —
 * never disappearing while the account's day could still be live (a backstop
 * must not eat live cards; server archival owns precision). Items without
 * parseable trigger data fall back to inserted_at same-local-day.
 */
function isWithinCheckinDay(item, now) {
    var parsed = CheckinTriggerDataSchema.safeParse(item.data);
    if (parsed.success) {
        return localDateString(now) <= parsed.data.local_date;
    }
    return isSameLocalDay(insertedAtDate(item), now);
}
/**
 * Items the coach still needs to see/act on: not yet interacted with and not
 * archived. The feed surfaces un-interacted items only — both a UX choice and
 * the compliance posture (no retained history); once a coach answers (or
 * another channel does, first-response-wins), the item drops out.
 *
 * Owner ruling 2 (2026-08-11): coach-facing question cards have NO day
 * roll-off — an unanswered question sits open until the coach answers it,
 * however old. Only the producer-facing warning card keeps the day cut-off
 * (see {@link CHECKIN_ROLLOFF_WORKFLOW_KEYS}).
 */
export function selectActiveFeedItems(items, now) {
    return items.filter(function (item) { return item.interacted_at === null &&
        item.archived_at === null &&
        (!isCheckinRolloffItem(item) || isWithinCheckinDay(item, now)); });
}
/**
 * Whether a check-in card should render live Yes/No buttons: ALWAYS, while it
 * is visible.
 *
 * Owner ruling 2 (2026-08-11) removed the shot clock this function used to
 * mirror. The backend's coach-stage timeout sweep is gone — a coach-facing
 * question sits open until the coach answers it, and the recorder accepts a
 * human answer over a machine `timeout` (SCO-517) — so a 60-minute client
 * cut-off would now be the ONLY thing declaring expiry, i.e. exactly the
 * self-resolution the ruling forbids. The no-zombie-buttons rule is upheld
 * the other way around: the buttons are live because the backend genuinely
 * accepts the answer. The (item, now) signature is kept so call sites and a
 * future visibility rule stay stable.
 */
export function isCheckinInteractive(_item, _now) {
    return true;
}
/**
 * Whether an expired check-in may be dismissed: never — there is no expired
 * state anymore (Owner ruling 2; see {@link isCheckinInteractive}). A
 * check-in leaves a surface via an answer or server archival, not a dismiss.
 */
export function isCheckinDismissable(_item, _now) {
    return false;
}
/**
 * The displayable body of a feed item: Knock's server-rendered HTML for its
 * textual (markdown/text) blocks, joined.
 *
 * Reads `rendered` (the Liquid-applied output) — NOT `content`, which is the
 * raw, un-interpolated template; reading `content` is what surfaced literal
 * `{{ data.producer_name }}` in the coach feed (SCO-406). The `action_url`
 * block is also `type: "markdown"` but is metadata (it makes the item
 * clickable), so it is excluded — otherwise its raw `{{ vars.app_url }}/…`
 * leaked into the body. `button_set` blocks are rendered as our own Yes/No UI.
 *
 * The returned HTML must be sanitized at the injection site (DOMPurify in
 * CoachKnockFeed) before rendering — `rendered` can embed values like
 * `producer_name` that a salesperson controls but a coach views. Defensive
 * against a malformed/missing blocks array.
 */
export function feedItemBodyHtml(blocks) {
    if (blocks === void 0) { blocks = []; }
    return blocks
        .filter(function (block) {
        var isTextual = block.type === 'markdown' || block.type === 'text';
        return (isTextual &&
            block.name !== ACTION_URL_BLOCK_NAME &&
            Boolean(block.rendered));
    })
        .map(function (block) { return block.rendered; })
        .join('\n');
}
/**
 * Whether a feed item is the rung-1 scoring nudge (SCO-504), the one producer
 * workflow whose tap goes to its own `action_url`. Owner contract: the tap
 * lands directly on the scoring screen, never the app root. The only other
 * card that opens anything is the midday, and only to this app's fixed
 * expectations page ({@link producerOpenUrl}). Other producer workflows also
 * carry an `action_url` block (producer-nudge and the warning card point at
 * the app root), but their tap still means "I heard you": do not generalize
 * navigation onto them.
 */
export function isScoringNudgeItem(item) {
    var _a;
    return ((_a = item.source) === null || _a === void 0 ? void 0 : _a.key) === PRODUCER_SCORING_NUDGE_WORKFLOW_KEY;
}
/**
 * The validated http(s) action URL of a feed item, or null. The `action_url`
 * block is excluded from {@link feedItemBodyHtml} as metadata — this is its
 * consumer. `rendered` is Knock's Liquid-applied output and can arrive
 * markdown-wrapped, so tags are stripped before validation; anything that
 * doesn't parse as an absolute http(s) URL (a relative path, a
 * `javascript:` payload from a compromised template) is dropped rather than
 * rendered tappable.
 */
export function feedItemActionUrl(blocks) {
    var _a;
    if (blocks === void 0) { blocks = []; }
    var block = blocks.find(function (b) { return b.name === ACTION_URL_BLOCK_NAME; });
    var raw = ((_a = block === null || block === void 0 ? void 0 : block.rendered) !== null && _a !== void 0 ? _a : '').replace(/<[^>]+>/g, '').trim();
    if (raw === '') {
        return null;
    }
    try {
        var url = new URL(raw);
        return url.protocol === 'http:' || url.protocol === 'https:' ?
            url.toString() :
            null;
    }
    catch (_b) {
        return null;
    }
}
/** The one producer card whose Open may go anywhere but an action_url (VC-02). */
export var PRODUCER_LINKED_BODY_WORKFLOW_KEYS = ['midday-producer'];
/**
 * Where the midday's "Your expectations are here" goes: this app's own
 * expectations page (the template links `{{ vars.app_url }}` plus this path).
 * Fixed here, never read from the body (James 2026-10-18, Q9).
 */
export var PRODUCER_EXPECTATIONS_PATH = '/mobile/authorized/expectations';
/**
 * Where a producer card's tap goes, or null when the tap only means "I heard
 * you": the scoring nudge's action_url (SCO-504), else, for the midday when
 * its body carries its link, this app's own expectations page (VC-02). No
 * other card opens anything, and no destination is read from a body: a body
 * can carry text someone typed. The surface acknowledges first and navigates
 * only once that lands.
 */
/**
 * The scoring nudge's action_url when the feed-link rule allows it (James
 * 2026-10-19, Q24 (b), B4: this app's origin, or exactly the API origin on a
 * signed-response path), else null. Open and the banner's ranking both use
 * it, so a nudge that opens nothing is never ranked as an ask.
 */
function allowedScoringActionUrl(blocks, origins) {
    var url = feedItemActionUrl(blocks);
    return url === null ? null : allowedFeedHref(url, origins);
}
export function producerOpenUrl(item, ctx) {
    var _a;
    if (isScoringNudgeItem(item)) {
        return allowedScoringActionUrl(item.blocks, { app: ctx.origin, api: ctx.apiEndpoint });
    }
    if (!PRODUCER_LINKED_BODY_WORKFLOW_KEYS.includes((_a = item.source) === null || _a === void 0 ? void 0 : _a.key)) {
        return null;
    }
    return ctx.hasLink(feedItemBodyHtml(item.blocks)) ? "".concat(ctx.origin).concat(PRODUCER_EXPECTATIONS_PATH) : null;
}
// --- Producer banner + bell selectors (launch requirement, locked 2026-07-02) ---
/** How long an Virtual Coach message stays visible in the producer bell. */
export var BELL_ROLLOFF_MS = 8 * 60 * 60 * 1000;
/** Whether a feed item was delivered TO a producer (banner/bell candidate). */
/**
 * Items a surface still owes a "shown" report for (SCO-702). Knock stamps
 * `seen_at` optimistically the moment `markAsSeen` is called, so filtering on
 * it is the whole dedupe: a second report of the same list is a no-op.
 */
export function selectUnseenItems(items) {
    return items.filter(function (item) { return item.seen_at === null; });
}
/** Items a surface still owes a "was on a visible screen" report for (SCO-702). */
export function selectUnreadItems(items) {
    return items.filter(function (item) { return item.read_at === null; });
}
export function isProducerFacingItem(item) {
    var _a;
    return PRODUCER_FACING_WORKFLOW_KEYS.includes((_a = item.source) === null || _a === void 0 ? void 0 : _a.key);
}
var insertedAtMs = function (item) { return Date.parse(item.inserted_at); };
/**
 * The DOM-free fallback for "does this body have words": the tags stripped.
 * Only decides emptiness, never what is shown. The surface passes the real
 * `feedBodyPlainText`, which also drops script and style contents.
 */
function bodyWordsWithoutDom(html) {
    return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;|&#160;/gi, ' ');
}
/**
 * Characters that draw nothing on their own and are not words (SCO-860):
 * zero-width space, non-joiner and joiner (U+200B–U+200D), the BOM
 * (U+FEFF), the soft hyphen (U+00AD), the combining grapheme joiner
 * (U+034F), the Mongolian vowel separator (U+180E), the word joiner and
 * invisible operators (U+2060–U+2064) and the variation selectors U+FE0E and
 * U+FE0F. `trim()` keeps all but the BOM, so a body of only these would
 * banner an empty card. Only the emptiness check strips them: what is shown
 * keeps them, so an emoji sequence or a joiner inside a word still renders.
 */
// The combining marks (U+034F, U+FE0E, U+FE0F) sit outside the class: inside
// one they read as joined to their neighbour (no-misleading-character-class).
var INVISIBLE = /[\u00AD\u180E\u200B-\u200D\u2060-\u2064\uFEFF]|\u034F|\uFE0E|\uFE0F/g;
/**
 * The bell's day-view: every producer-facing message from the last 8 hours,
 * acknowledged or not (same-day "what was said and when"), newest first. An
 * item exactly 8 hours old has rolled off. No retained history beyond the
 * window — deliberate product/compliance posture, same as the coach feed.
 */
export function selectBellItems(items, now, 
/**
 * The text the surface will show for a body (`feedBodyPlainText`). The
 * surface passes it so "is there anything to show" tests exactly what is
 * shown (fresh review P3-1). Without it, a DOM-free fallback strips the
 * tags, so these helpers stay usable without a DOM.
 */
bodyText) {
    if (bodyText === void 0) { bodyText = bodyWordsWithoutDom; }
    var cutoffMs = now.getTime() - BELL_ROLLOFF_MS;
    return items
        .filter(function (item) { return isProducerFacingItem(item) &&
        item.archived_at === null &&
        insertedAtMs(item) > cutoffMs &&
        // A misconfigured workflow can deliver an item with no textual
        // body (only an action_url block, or markup with no words such as
        // a lone image): without this guard it banners as avatars +
        // "Tap to dismiss" around an empty message. Checked on the text
        // that is shown, not on the HTML.
        bodyText(feedItemBodyHtml(item.blocks)).replace(INVISIBLE, '').trim() !== ''; })
        .sort(function (a, b) { return insertedAtMs(b) - insertedAtMs(a); });
}
/**
 * A producer card that asks for something (Q7): the producer's own check-in
 * Yes/No, or a card whose tap is an action (the scoring nudge's action_url).
 * Every other producer card is informational, the voice's kickoff and midday
 * included.
 */
export function isProducerAskItem(item, origins) {
    return isProducerCheckinItem(item) ||
        (isScoringNudgeItem(item) && allowedScoringActionUrl(item.blocks, origins) !== null);
}
/**
 * Producer cards that are not asks but rank with them for the banner (James
 * 2026-10-19, Q12 (a)): the rung-2 warning, which is sent precisely because
 * the check-in is unanswered, and a coach's own nudge (a coach pressed Send).
 */
export var PRODUCER_PRIORITY_WORKFLOW_KEYS = ['producer-checkin-warning', 'coach-nudge'];
/**
 * A priority card: an ask ({@link isProducerAskItem}), the warning or the
 * coach-nudge. Among priority cards the newest takes the banner; every other
 * card is informational and waits in the bell while one is unheard.
 */
export function isProducerPriorityItem(item, origins) {
    var _a;
    return isProducerAskItem(item, origins) ||
        PRODUCER_PRIORITY_WORKFLOW_KEYS.includes((_a = item.source) === null || _a === void 0 ? void 0 : _a.key);
}
/**
 * The item the persistent top banner shows, among the producer-facing items
 * inside the 8h window that have not been acknowledged from any surface
 * (first-acknowledgment-wins — `interacted_at` is server-side Knock state, so
 * a tap on the phone clears the desktop banner via the realtime store): the
 * NEWEST PRIORITY card if any is unheard (an ask, the warning, a coach-nudge;
 * Q7, Q12), otherwise the newest unheard card. Null → no banner.
 *
 * The window matters: without it, yesterday's un-acknowledged pace nudge
 * ("12 to go!") would banner as fresh coaching the next morning — stale,
 * wrong, and against the locked "gone by tomorrow" intent. An un-heard nudge
 * simply expires with the window; the recap covers the day.
 */
export function selectBannerItem(items, now, 
/**
 * This app's and the Node API's origins: a scoring nudge is an ask only
 * when its Open is allowed (Q24).
 */
origins, bodyText) {
    var _a, _b;
    if (bodyText === void 0) { bodyText = bodyWordsWithoutDom; }
    // Derived from the bell's day-view (already producer-filtered, windowed,
    // and sorted newest-first) so the two surfaces share ONE eligibility rule
    // and can never diverge.
    var unheard = selectBellItems(items, now, bodyText)
        .filter(function (item) { return item.interacted_at === null; });
    // James 2026-10-18 (Q7) and 2026-10-19 (Q12 (a)): an unheard priority
    // card (the producer's check-in Yes/No, a card with an action such as the
    // scoring nudge, the rung-2 warning, a coach-nudge) keeps the banner over
    // newer informational cards, which wait in the bell; among priority cards
    // the newest wins (`unheard` is newest first). With none unheard, the
    // banner is the newest un-heard card, as before.
    return (_b = (_a = unheard.find(function (item) { return isProducerPriorityItem(item, origins); })) !== null && _a !== void 0 ? _a : unheard[0]) !== null && _b !== void 0 ? _b : null;
}
/**
 * Trigger `data` is external input (set by the backend, transported by Knock),
 * so it's parsed rather than trusted. Anything malformed degrades to nulls —
 * the banner falls back to initials, never throws.
 */
var FeedItemAvatarDataSchema = z.object({
    producer_avatar_url: z.string().nullable().optional(),
    coach_avatar_url: z.string().nullable().optional()
});
export function feedItemAvatars(item) {
    var _a, _b;
    var parsed = FeedItemAvatarDataSchema.safeParse(item.data);
    if (!parsed.success) {
        return { producerAvatarUrl: null, coachAvatarUrl: null };
    }
    return {
        producerAvatarUrl: (_a = parsed.data.producer_avatar_url) !== null && _a !== void 0 ? _a : null,
        coachAvatarUrl: (_b = parsed.data.coach_avatar_url) !== null && _b !== void 0 ? _b : null
    };
}
